'use client';

import { Progress as ProgressPrimitive } from '@base-ui/react/progress';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const progressTrackVariants = cva('relative w-full overflow-hidden rounded-full', {
  variants: {
    size: {
      sm: 'h-1',
      default: 'h-2',
      lg: 'h-4',
    },
    variant: {
      default: 'bg-primary/20',
      success: 'bg-success-fill/20',
      warning: 'bg-warning-fill/20',
      destructive: 'bg-destructive-fill/20',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

const progressIndicatorVariants = cva(
  'h-full rounded-[inherit] transition-all data-indeterminate:w-2/5 data-indeterminate:animate-progress-indeterminate',
  {
    variants: {
      variant: {
        default: 'bg-primary',
        success: 'bg-success-fill',
        warning: 'bg-warning-fill',
        destructive: 'bg-destructive-fill',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type ProgressProps = ProgressPrimitive.Root.Props &
  VariantProps<typeof progressTrackVariants> & {
    className?: string;
  };

const Progress = ({ className, children, size = 'default', variant = 'default', ...props }: ProgressProps) => {
  return (
    <ProgressPrimitive.Root data-slot="progress" className={cn('relative', className)} {...props}>
      {children ?? (
        <ProgressTrack size={size} variant={variant}>
          <ProgressIndicator variant={variant} />
        </ProgressTrack>
      )}
    </ProgressPrimitive.Root>
  );
};

type ProgressTrackProps = ProgressPrimitive.Track.Props &
  VariantProps<typeof progressTrackVariants> & {
    className?: string;
  };

const ProgressTrack = ({ className, size = 'default', variant = 'default', ...props }: ProgressTrackProps) => {
  return (
    <ProgressPrimitive.Track
      data-slot="progress-track"
      className={cn(progressTrackVariants({ size, variant, className }))}
      {...props}
    />
  );
};

type ProgressIndicatorProps = ProgressPrimitive.Indicator.Props &
  VariantProps<typeof progressIndicatorVariants> & {
    className?: string;
  };

const ProgressIndicator = ({ className, variant = 'default', ...props }: ProgressIndicatorProps) => {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(progressIndicatorVariants({ variant, className }))}
      {...props}
    />
  );
};

type ProgressLabelProps = ProgressPrimitive.Label.Props & {
  className?: string;
};

const ProgressLabel = ({ className, ...props }: ProgressLabelProps) => {
  return (
    <ProgressPrimitive.Label
      data-slot="progress-label"
      className={cn('font-medium text-sm leading-none', className)}
      {...props}
    />
  );
};

type ProgressValueProps = ProgressPrimitive.Value.Props & {
  className?: string;
};

const ProgressValue = ({ className, ...props }: ProgressValueProps) => {
  return (
    <ProgressPrimitive.Value
      data-slot="progress-value"
      className={cn('ml-auto font-medium text-muted-foreground text-sm tabular-nums', className)}
      {...props}
    />
  );
};

const progressCircleVariants = cva('relative inline-flex shrink-0 items-center justify-center', {
  variants: {
    size: {
      sm: 'size-12',
      default: 'size-20',
      lg: 'size-36',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const progressCircleTrackVariants = cva('', {
  variants: {
    variant: {
      default: 'stroke-primary/20',
      success: 'stroke-success-fill/20',
      warning: 'stroke-warning-fill/20',
      destructive: 'stroke-destructive-fill/20',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const progressCircleIndicatorVariants = cva('transition-[stroke-dashoffset] duration-700 ease-out', {
  variants: {
    variant: {
      default: 'stroke-primary',
      success: 'stroke-success-fill',
      warning: 'stroke-warning-fill',
      destructive: 'stroke-destructive-fill',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type ProgressCircleProps = ProgressPrimitive.Root.Props &
  VariantProps<typeof progressCircleVariants> &
  VariantProps<typeof progressCircleTrackVariants> & {
    className?: string;
  };

const ProgressCircle = ({
  className,
  children,
  size = 'default',
  variant = 'default',
  value,
  min = 0,
  max = 100,
  ...props
}: ProgressCircleProps) => {
  const percent = value == null ? 0 : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <ProgressPrimitive.Root
      data-slot="progress-circle"
      value={value}
      min={min}
      max={max}
      className={cn(progressCircleVariants({ size, className }))}
      {...props}
    >
      <svg viewBox="0 0 100 100" className="absolute inset-0 size-full -rotate-90" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r="44"
          fill="none"
          strokeWidth="7"
          className={progressCircleTrackVariants({ variant })}
        />
        {value != null && (
          <circle
            data-slot="progress-circle-indicator"
            cx="50"
            cy="50"
            r="44"
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={100}
            strokeDashoffset={100 - percent}
            className={progressCircleIndicatorVariants({ variant })}
          />
        )}
      </svg>
      {children && <div className="relative flex flex-col items-center justify-center text-center">{children}</div>}
    </ProgressPrimitive.Root>
  );
};

export {
  Progress,
  ProgressCircle,
  ProgressIndicator,
  ProgressLabel,
  ProgressTrack,
  ProgressValue,
  progressCircleIndicatorVariants,
  progressCircleTrackVariants,
  progressCircleVariants,
  progressIndicatorVariants,
  progressTrackVariants,
};
